import React from "react";
import millify from "millify";
import CountUp from "react-countup";
import { CurrencyCode } from "@core";
import { FormatterUtils } from "@utils";
import { useGlobalQuery } from "@hooks";

export interface IFormattedValueProps {
  animated: boolean;
  formatLocale: string;
  formatCurrency: number;
  value: string | number;
  formatPrecision: number;
  formatAbbreviate: boolean;
  formatType: "number" | "currency" | "percentage";
}

const FormattedValue = ({
  value,
  animated,
  formatType,
  formatLocale,
  formatAbbreviate,
  formatPrecision: precision,
  formatCurrency: currencyId,
}: IFormattedValueProps) => {
  const currency = useGlobalQuery("Currencies", "getById", currencyId, {
    enabled: !!currencyId,
  });

  const formattingFn = React.useMemo(() => {
    return (count: number) => {
      const formatter = FormatterUtils[formatType] || FormatterUtils.number;
      let formattedValue = formatter(count, {
        precision,
        locale: currency.data?.Locale ?? formatLocale,
        currencyIso: (currency.data?.NombreISO ??
          "DOP") as keyof typeof CurrencyCode,
      });

      if (formatAbbreviate) {
        if (formatType === "currency") {
          const numericValue = parseFloat(
            formattedValue.replace(/[^0-9.-]+/g, ""),
          );
          const millifiedValue = millify(numericValue, {
            precision: precision || 2,
          });
          formattedValue = formattedValue.replace(/[0-9.,]+/, millifiedValue);
        } else {
          formattedValue = millify(count, { precision: precision || 2 });
        }
      }

      return formattedValue;
    };
  }, [
    currency.data?.Locale,
    currency.data?.NombreISO,
    formatAbbreviate,
    formatLocale,
    formatType,
    precision,
  ]);

  const formatWithoutAbbreviation = React.useMemo(() => {
    return (count: string | number) => {
      const formatter = FormatterUtils[formatType] || FormatterUtils.number;
      return formatter(parseFloat(`${count}`), {
        precision,
        locale: currency.data?.Locale ?? formatLocale,
        currencyIso: (currency.data?.NombreISO ??
          "DOP") as keyof typeof CurrencyCode,
      });
    };
  }, [
    currency.data?.Locale,
    currency.data?.NombreISO,
    formatLocale,
    formatType,
    precision,
  ]);

  return (
    <span title={formatWithoutAbbreviation(value)}>
      {typeof value === "string" ? (
        value
      ) : (
        <>
          {animated ? (
            <CountUp end={value} duration={2} formattingFn={formattingFn} />
          ) : (
            formattingFn(value)
          )}
        </>
      )}
    </span>
  );
};

export default FormattedValue;
